import {
  Component,
  input,
  output,
  signal,
  computed,
  HostListener,
  TemplateRef,
  ContentChild,
  ElementRef,
  inject,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  iconoirNavArrowLeft,
  iconoirNavArrowRight,
  iconoirXmark,
} from '@ng-icons/iconoir';

export interface CarouselItem {
  id: string | number;
  imageUrl: string;
  title?: string;
  subtitle?: string;
}

@Component({
  selector: 'app-carousel',
  standalone: true,
  imports: [CommonModule, NgIcon],
  providers: [
    provideIcons({ iconoirNavArrowLeft, iconoirNavArrowRight, iconoirXmark }),
  ],
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
})
export class CarouselComponent {
  private el = inject(ElementRef);

  private get isRtl(): boolean {
    return getComputedStyle(this.el.nativeElement).direction === 'rtl';
  }

  // Inputs
  items = input<CarouselItem[]>([]);
  loading = input<boolean>(false);
  showIndicators = input<boolean>(true);
  showPreview = input<boolean>(true);
  placeholderUrl = input<string>('');
  emptyMessage = input<string>('No items available');

  // Outputs
  slideChange = output<number>();

  // Content projection for custom slide content
  @ContentChild('slideContent') slideContentTemplate?: TemplateRef<any>;
  @ContentChild('slideInfo') slideInfoTemplate?: TemplateRef<any>;

  // ViewChild for carousel container (for potential future use, e.g., swipe handling)
  @ViewChild('carouselContainer')
  carouselContainer?: ElementRef<HTMLDivElement>;

  // Width and height to manitain aspect ratio and prevent layout shift
  aspectRatio = signal<number>(16 / 9); // Default aspect ratio
  aspectRatioSet = signal<boolean>(false); // Default aspect ratio

  // Internal state
  currentIndex = signal<number>(0);
  previewOpen = signal<boolean>(false);
  imageLoading = signal<boolean>(true);
  previewImageLoading = signal<boolean>(true);

  // Computed
  totalItems = computed(() => this.items().length);

  currentItem = computed(() => {
    const itemsList = this.items();
    const index = this.currentIndex();
    return itemsList[index] || null;
  });

  hasNext = computed(() => this.currentIndex() < this.totalItems() - 1);
  hasPrev = computed(() => this.currentIndex() > 0);

  // Keyboard navigation for preview modal (RTL-aware)
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (!this.previewOpen()) return;

    switch (event.key) {
      case 'Escape':
        this.closePreview();
        break;
      case 'ArrowLeft':
        // In RTL, left arrow should go next; in LTR, it should go prev
        this.isRtl ? this.next() : this.prev();
        break;
      case 'ArrowRight':
        // In RTL, right arrow should go prev; in LTR, it should go next
        this.isRtl ? this.prev() : this.next();
        break;
    }
  }

  // Navigation
  prev(): void {
    if (this.hasPrev()) {
      this.imageLoading.set(true);
      this.previewImageLoading.set(true);
      this.currentIndex.set(this.currentIndex() - 1);
      this.slideChange.emit(this.currentIndex());
    }
  }

  next(): void {
    if (this.hasNext()) {
      this.imageLoading.set(true);
      this.previewImageLoading.set(true);
      this.currentIndex.set(this.currentIndex() + 1);
      this.slideChange.emit(this.currentIndex());
    }
  }

  goTo(index: number): void {
    if (index >= 0 && index < this.totalItems()) {
      this.imageLoading.set(true);
      this.previewImageLoading.set(true);
      this.currentIndex.set(index);
      this.slideChange.emit(index);
    }
  }

  // Reset to first item
  reset(): void {
    this.currentIndex.set(0);
    this.imageLoading.set(true);
  }

  // Preview modal
  openPreview(): void {
    if (this.showPreview() && !this.imageLoading()) {
      this.previewOpen.set(true);
      this.previewImageLoading.set(true);
    }
  }

  closePreview(): void {
    this.previewOpen.set(false);
  }

  // Image load handlers
  onImageLoad(): void {
    this.imageLoading.set(false);

    if (!this.aspectRatioSet()) {
      this.aspectRatio.set(
        this.carouselContainer?.nativeElement.clientWidth! /
          this.carouselContainer?.nativeElement.clientHeight!,
      );
      this.aspectRatioSet.set(true);
    }
  }

  onImageError(event: Event): void {
    this.imageLoading.set(false);
    const target = event.target as HTMLImageElement;
    if (this.placeholderUrl()) {
      target.src = this.placeholderUrl();
    }
  }

  onPreviewImageLoad(): void {
    this.previewImageLoading.set(false);
  }

  onPreviewImageError(event: Event): void {
    this.previewImageLoading.set(false);
    const target = event.target as HTMLImageElement;
    if (this.placeholderUrl()) {
      target.src = this.placeholderUrl();
    }
  }

  getImageUrl(item: CarouselItem): string {
    return item.imageUrl || this.placeholderUrl();
  }
}
